import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import { EventEmitterService } from '../../../services/event-emitter.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../../../services/authentication.service';
import { UsersService } from '../../../services/users.service';
import Plans from '../../../models/plans.json';
import Languages from '../../../models/languages.json';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ResetPasswordComponent } from '../../../modals/reset-password/reset-password.component';
import { DeleteAccountComponent } from '../../../modals/delete-account/delete-account.component';
import { DeletePopupComponent } from '../../../modals/delete-popup/delete-popup.component';
import { BusinessCategoryPopupComponent } from '../../../modals/business-category-popup/business-category-popup.component';
import { TranslateService } from '@ngx-translate/core';
import Currencies from '../../../models/currencies.json';
import { MessagePopupComponent } from '../../../modals/message-popup/message-popup.component';
import { AccountTypes } from '../../../models/account_types';
import Categories from '../../../models/categories.json';
import { BusinessesService } from '../../../services/businesses.service';

@Component({
  standalone: false,
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  user;
  business;
  formGroup: FormGroup;
  isFormChanged = false;
  pricingPlans: any = Plans;
  filteredPricingPlans: any = Plans;
  languages: any = Languages;
  currencies: any = Currencies;
  paymentOptions = [
    { label: 'pay-on-delivery', value: 'pay-on-delivery' },
    // { label: 'pay-with-credit-card', value: 'pay-with-credit-card' },
  ];
  accountTypes = AccountTypes;
  categories: any = Categories.filter((category: any) => category.id !== 0);

  businessCategories: any[] = [];
  banners: any[] = [];
  maxBanners = 0;
  bannerUploading = false;
  destroyRef = inject(DestroyRef);

  constructor(
    public eventEmitterService: EventEmitterService,
    private usersService: UsersService,
    private businessesService: BusinessesService,
    private modalService: BsModalService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
  ) {
    this.eventEmitterService.invokeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event == 'call-all-business-categories') {
          this.getBusinessCategories();
        }
      });
  }

  async ngOnInit() {
    await this.getUser();
    this.maxBanners =
      this.user.user.business.type == AccountTypes.Delivery ? 10 : 1;
    this.getMineBusiness();
    this.getBusinessCategories();
    this.getBusinessBanners();
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  getMineBusiness() {
    this.usersService.getMineBusiness().subscribe((response) => {
      console.log('response:');
      console.log(response);
      this.business = response;
      localStorage.setItem('businessName', this.business.name);
      localStorage.setItem(
        this.business.name + '-currency',
        this.business.currency,
      );
      this.updatePlansForSelectedType();
      this.setupForm();
    });
  }

  setupForm() {
    console.log('this.business:');
    console.log(this.business);
    const storedLang = localStorage.getItem('language');

    this.languages.forEach((item) => {
      if (item.id == storedLang) {
        this.business.language = item;
      }
    });
    this.pricingPlans.forEach((item) => {
      if (item.id == this.business.plan) {
        this.business.plan = item;
      }
    });
    this.currencies.forEach((item) => {
      if (item.id == this.business.currency) {
        this.business.currency = item;
      }
    });

    // Set default payment options if not set
    if (!this.business.payment_options) {
      this.business.payment_options = ['pay-on-delivery'];
    } else if (typeof this.business.payment_options === 'string') {
      try {
        this.business.payment_options = JSON.parse(
          this.business.payment_options,
        );
      } catch (e) {
        this.business.payment_options = ['pay-on-delivery'];
      }
    }

    // Resolve saved category ids into the matching category objects
    if (!this.business.categories) {
      this.business.categories = [];
    } else if (typeof this.business.categories === 'string') {
      try {
        const categoryIds = JSON.parse(this.business.categories);
        this.business.categories = this.categories.filter((category: any) =>
          categoryIds.includes(category.id),
        );
      } catch (e) {
        this.business.categories = [];
      }
    }

    this.formGroup = new FormGroup({
      display_name: new FormControl(
        this.business ? this.business.display_name : '',
        Validators.required,
      ),
      name: new FormControl(this.business ? this.business.name : '', [
        Validators.required,
        Validators.pattern(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/),
      ]),
      email: new FormControl(
        this.business ? this.business.email : '',
        Validators.required,
      ),
      imageURL: new FormControl(this.business ? this.business.imageURL : ''),
      file: new FormControl(null),

      plan: new FormControl(this.business ? this.business.plan : ''),

      language: new FormControl(this.business ? this.business.language : ''),

      currency: new FormControl(this.business ? this.business.currency : ''),

      payment_options: new FormControl(
        this.business ? this.business.payment_options : ['pay-on-delivery'],
        [Validators.required, Validators.minLength(1)],
      ),

      categories: new FormControl(
        this.business ? this.business.categories : [],
        [Validators.maxLength(3)],
      ),

      instagramUrl: new FormControl(
        this.business ? this.business.instagramUrl : '',
      ),
      facebookUrl: new FormControl(
        this.business ? this.business.facebookUrl : '',
      ),
      tiktokUrl: new FormControl(this.business ? this.business.tiktokUrl : ''),

      // Add new form controls for colors
      primary_color: new FormControl(
        this.business.primary_color,
        Validators.required,
      ),
      secondary_color: new FormControl(
        this.business.secondary_color,
        Validators.required,
      ),
    });

    this.formGroup.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });

    this.formGroup.get('name').valueChanges.subscribe((value: string) => {
      if (!value) {
        return;
      }
      const formatted = value.toLowerCase().replace(/ /g, '_');
      if (formatted !== value) {
        this.formGroup.get('name').setValue(formatted, { emitEvent: false });
      }
    });
  }

  checkFormChanges() {
    if (this.formGroup.dirty) {
      this.isFormChanged = true;
    } else {
      this.isFormChanged = false;
    }
  }

  updatePlansForSelectedType() {
    if (this.user.user.business.type == AccountTypes.Delivery) {
      this.filteredPricingPlans = this.pricingPlans.filter(
        (plan: any) => plan.id == 1,
      );
    } else {
      this.filteredPricingPlans = this.pricingPlans;
    }
  }

  setFlow(event) {
    this.formGroup.get('flow').setValue(event.value);
  }

  setPlan(event) {
    this.formGroup.get('plan').setValue(event.value);
  }

  resetPasswordClicked() {
    this.modalService.show(ResetPasswordComponent, {
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  deleteAccountClicked() {
    this.modalService.show(DeleteAccountComponent, {
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  setCurrency(event) {
    this.formGroup.get('currency').setValue(event.value);
  }

  getBusinessCategories() {
    this.businessesService.getAllBusinessCategories({}).subscribe(
      (response: any) => {
        this.businessCategories = response;
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  addBusinessCategoryClicked() {
    this.modalService.show(BusinessCategoryPopupComponent, {
      initialState: {
        title: 'new-business-category',
        type: 'new-business-category',
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  editBusinessCategoryClicked(category) {
    this.modalService.show(BusinessCategoryPopupComponent, {
      initialState: {
        title: 'edit-business-category',
        type: 'edit-business-category',
        category: category,
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  deleteBusinessCategoryPopup(id) {
    this.modalService.show(DeletePopupComponent, {
      initialState: {
        title: 'delete-business-category',
        text: 'delete-business-category-text',
        id: id,
        onButtonClick: this.deleteBusinessCategory.bind(this),
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  async deleteBusinessCategory(id): Promise<void> {
    return new Promise((resolve, reject) => {
      this.businessesService.deleteBusinessCategory(id).subscribe(
        () => {
          this.getBusinessCategories();
          resolve();
        },
        (error) => {
          console.log('error:');
          console.log(error);
          reject();
        },
      );
    });
  }

  getBusinessBanners() {
    this.businessesService.getAllBusinessBanners({}).subscribe(
      (response: any) => {
        this.banners = response;
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  onBannerFileSelected(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    if (!fileInput.files || fileInput.files.length === 0) {
      return;
    }

    const file = fileInput.files[0];
    const bannerFormData = new FormData();
    bannerFormData.append('file', file);

    this.bannerUploading = true;
    this.businessesService.createBusinessBanner(bannerFormData).subscribe(
      () => {
        fileInput.value = '';
        this.bannerUploading = false;
        this.getBusinessBanners();
      },
      (error) => {
        console.log('error:');
        console.log(error);
        fileInput.value = '';
        this.bannerUploading = false;
      },
    );
  }

  deleteBusinessBannerClicked(id) {
    this.businessesService.deleteBusinessBanner(id).subscribe(
      () => {
        this.getBusinessBanners();
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  saveChanges() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }

    localStorage.setItem('language', this.formGroup.get('language').value.id);
    this.translateService.use(this.formGroup.get('language').value.id);

    // Convert payment_options array to string
    const apiData = { ...this.formGroup.value };
    apiData.currency = this.formGroup.get('currency').value.id;
    apiData.payment_options = JSON.stringify(apiData.payment_options);
    apiData.categories = JSON.stringify(
      (apiData.categories || []).map((category: any) => category.id),
    );

    const formData = new FormData();

    let imageURL = JSON.stringify(apiData.imageURL);
    imageURL = JSON.parse(imageURL);

    if (this.isBase64(imageURL)) {
      imageURL = '';
    }

    Object.keys(apiData).forEach((key) => {
      if (key === 'imageURL') {
        formData.append(key, imageURL);
      } else if (key !== 'plan') {
        formData.append(key, apiData[key]);
      }
    });

    formData.append('plan_id', apiData.plan.id);

    console.log('formData:');
    console.log(formData);

    this.usersService.saveSettings(formData).subscribe(
      (response) => {
        console.log('response:');
        console.log(response);
        this.isFormChanged = false;

        // After saving settings, refresh the token
        this.usersService.refreshToken().subscribe(
          (tokenResponse: any) => {
            // Update token in localStorage
            localStorage.setItem('token', tokenResponse.token);
            // Update user data with new token
            this.authenticationService.completeAuthentication(tokenResponse);
            window.location.reload();
          },
          (error) => {
            console.log('Error refreshing token:', error);
            this.modalService.show(MessagePopupComponent, {
              initialState: {
                title: 'relogin-after-plan-change',
                text: 'relogin-after-plan-change-text',
                buttonText: 'okay',
              },
              class: 'alert-modal modal-dialog-centered',
              backdrop: 'static',
            });
            this.authenticationService.logout();
          },
        );
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  isBase64(dataUrl: string): boolean {
    if (!dataUrl) {
      return false;
    }
    const base64Regex = /^data:(.*?);base64,(.*)$/;
    const match = dataUrl.match(base64Regex);

    if (!match) {
      return false;
    }

    const base64String = match[2];
    const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;

    return base64Pattern.test(base64String) && base64String.length % 4 === 0;
  }
}
